'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

// `import type` from a `'server-only'` module is safe — TypeScript erases
// type-only imports before bundling, so the server-only guard never runs.
import type { MyMembershipRow } from '~/(tenant)/memberships/_lib/server/memberships-page.loader';

/**
 * The signed-in user's (and linked children's) memberships at this tenant.
 * Mirrors `loadMyMembershipsForTenant` on the server — same RPC, same shape.
 * The RPC authorizes self and tenant-linked children from the authenticated
 * subject before returning its safe projection. Shared by the native home
 * "my memberships" section and the check-in sheet, so both read one cache
 * entry.
 */
export function useMyMemberships(accountId: string) {
  const client = useSupabase();

  const { data: session, isPending: isSessionPending } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const userId = session?.user?.id;

  const membershipsQuery = useQuery({
    queryKey: ['native-home-my-memberships', accountId, userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<MyMembershipRow[]> => {
      const { data, error } = await client.rpc('get_my_memberships', {
        p_account_id: accountId,
        p_include_children: true,
      });

      if (error) throw new Error(error.message);
      return (data ?? []) as MyMembershipRow[];
    },
  });

  return {
    userId,
    isSessionPending,
    memberships: membershipsQuery.data,
    isPending: Boolean(userId) && membershipsQuery.isPending,
    isError: membershipsQuery.isError,
  };
}
